import React, { forwardRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, XIcon } from '@heroicons/react/solid';
import Asterix from '../../assets/asterix.svg';
import { Input, InputContainer, Label, TextArea } from '../AccountProfile/ProfileStyles';
import styled from 'styled-components/macro';
import { Switch, ToggleButton } from '../shared/Fields/Switch';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import {
  METADATA_INSTANCE_DATA_TYPE_USER_INPUTS,
  METADATA_OBJECT_DATA_TYPE_ENUM,
  METADATA_OBJECT_DATA_TYPE_BOOLEAN,
  METADATA_OBJECT_DATA_TYPE_DATE,
  METADATA_OBJECT_DATA_TYPE_DATETIME,
  METADATA_OBJECT_DATA_TYPE_INTEGER,
  METADATA_OBJECT_DATA_TYPE_NUMBER,
  METADATA_OBJECT_DATA_TYPE_OBJECT,
  METADATA_OBJECT_DATA_TYPE_OBJECT_ARRAY,
  METADATA_OBJECT_DATA_TYPE_STRING_ARRAY,
  METADATA_OBJECT_DATA_TYPE_TIME,
  METADATA_OBJECT_DATA_TYPE_STRING,
  METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_TYPES,
  METADATA_OBJECT_PRIMITIVE_ARRAY_DATA_MAP,
  METADATA_OBJECT_DATA_TYPE_DATE_ARRAY,
  METADATA_OBJECT_DATA_TYPE_DATETIME_ARRAY,
  METADATA_OBJECT_DATA_TYPE_TIME_ARRAY,
  METADATA_OBJECT_DATA_TYPE_NUMBER_ARRAY,
  METADATA_OBJECT_DATA_TYPE_INTEGER_ARRAY
} from './metadataUtil';
import InputField from '../shared/Fields/InputField';
import { AppConstants } from '../../constants/AppConstants';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import { cloneDeep, isArray, isEqual, isUndefined } from 'lodash';
import InfoPopover from '../shared/Fields/InfoPopover';
import { PlusCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import PillsButton from '../shared/Fields/PillsButton';
import { removeArrayElement } from '../../util/consent-utils';

const DEFAULT_KEY_ID = 'key';
const DEFAULT_VALUE_KEY = 'value';

const ErrorLabel = styled.span`
  fontsize: 11px;
  color: red;
`;
interface DefaultValueInputProps {
  disabled: boolean;
  formData: any;
  setFormData: any;
}

const DefaultValueInput: React.FC<DefaultValueInputProps> = ({
  formData,
  setFormData,
  disabled
}) => {
  const { t } = useTranslation();
  const {
    name,
    type,
    isRequired,
    editable,
    searchable,
    sortable,
    maxLength,
    min,
    max,
    attributes,
    isNew,
    enumTypes,
    minItems,
    maxItems,
    default: defaultItems
  } = formData;

  const [pillIndex, setPillIndex] = useState(-1);

  const [formError, setFormError] = useState<any>({ default: '' });

  const [tempDate, setTempDate] = useState<any>('');

  const parseDate = (date: any, forDisplay: boolean = false) => {
    if (!date) {
      return '';
    }
    switch (type) {
      case METADATA_OBJECT_DATA_TYPE_DATE:
      case METADATA_OBJECT_DATA_TYPE_DATE_ARRAY:
        return moment(date).format(
          forDisplay
            ? AppConstants.DEFAULT_METADATA_INSTANCE_DATE_FORMAT_DISPLAY
            : AppConstants.DEFAULT_METADATA_INSTANCE_DATE_FORMAT
        );
      case METADATA_OBJECT_DATA_TYPE_DATETIME:
      case METADATA_OBJECT_DATA_TYPE_DATETIME_ARRAY:
        return !forDisplay
          ? moment(date).utc().format(AppConstants.DEFAULT_METADATA_INSTANCE_UTC_DATETIME_FORMAT)
          : moment(date).format(AppConstants.DEFAULT_METADATA_INSTANCE_DATETIME_FORMAT_DISPLAY);
      case METADATA_OBJECT_DATA_TYPE_TIME:
      case METADATA_OBJECT_DATA_TYPE_TIME_ARRAY:
        return !forDisplay
          ? moment(date).format(AppConstants.DEFAULT_METADATA_INSTANCE_TIME_FORMAT)
          : moment(date, AppConstants.DEFAULT_METADATA_INSTANCE_TIME_FORMAT).format(
              AppConstants.DEFAULT_METADATA_INSTANCE_TIME_FORMAT_DISPLAY
            );
      default:
        return '';
    }
  };

  const onDateChange = (date: any, isDismiss: boolean) => {
    switch (type) {
      case METADATA_OBJECT_DATA_TYPE_DATE:
      case METADATA_OBJECT_DATA_TYPE_DATETIME:
      case METADATA_OBJECT_DATA_TYPE_TIME:
        if (!isDismiss) {
          setTempDate(parseDate(date));
        } else {
          setFormData({
            ...formData,
            default: tempDate
          });
          setTempDate('');
        }
        break;
      case METADATA_OBJECT_DATA_TYPE_DATE_ARRAY:
      case METADATA_OBJECT_DATA_TYPE_DATETIME_ARRAY:
      case METADATA_OBJECT_DATA_TYPE_TIME_ARRAY:
        if (!isDismiss) {
          setTempDate(parseDate(date));
        } else {
          if (tempDate) {
            let arr = cloneDeep(defaultItems);
            if (pillIndex !== -1) {
              if (isArray(arr) && arr?.length) {
                arr[pillIndex] = tempDate;
                setFormData({
                  ...formData,
                  default: arr
                });
              } else {
                setFormData({
                  ...formData,
                  default: [tempDate]
                });
              }
              setTempDate('');
              setPillIndex(-1);
            } else {
              setFormData({
                ...formData,
                default: arr.length ? [...arr, tempDate] : [tempDate]
              });
              setTempDate('');
              setPillIndex(-1);
            }
          }
        }

        break;
    }
  };

  const removeDefaultArrItem = (index: number) => {
    let clonedArr = cloneDeep(defaultItems);
    clonedArr.splice(index, 1);
    setFormData({
      ...formData,
      default: clonedArr
    });
  };

  const renderPrimitiveField = () => {
    const DateInputField = forwardRef(({ value, onClick }: any, ref) => (
      <Input
        type={'text'}
        id={name}
        name={name}
        className="!w-full rounded border px-3 py-2"
        // required={isRequired}
        value={parseDate(defaultItems, true)}
        onClick={onClick}
        ref={ref}
        onChange={(e: any) => {}}
        data-testid="datetime-input"
        disabled={disabled}
      />
    ));

    const DateInputFieldArray = forwardRef(({ values, onClick, setValues }: any, ref: any): any => (
      <InputContainer
        id={name}
        className="!h-auto min-h-[44px] !w-full rounded border px-3 py-2"
        onClick={onClick}
        ref={ref}
        data-testid="datetime-input"
        disabled={disabled}
      >
        <PillsButton
          pillId={'date-default-pill'}
          pillMaxWidth={'100%'}
          rows={2}
          pillsData={isArray(values) ? values?.map((value: any) => ({ name: value })) : []}
          removeFilter={(item: any, index: number, event: any) => {
            event.stopPropagation();
            let arr = cloneDeep(values);
            if (index !== -1) {
              arr.splice(index, 1);
            }
            setFormData({ ...formData, default: arr });
          }}
          onClick={(index: number) => {
            setPillIndex(index);
            setTempDate(values[index]);
            onClick();
          }}
        ></PillsButton>
      </InputContainer>
    ));

    const boolValue: boolean = /true/i.test(defaultItems);

    switch (type) {
      case METADATA_OBJECT_DATA_TYPE_BOOLEAN:
        return (
          <div className="flex flex-row">
            <Switch
              toggle={boolValue}
              onClick={() => setFormData({ ...formData, default: !boolValue })}
              data-testid={'boolean-field'}
              id={name}
            >
              <ToggleButton toggle={boolValue} />
            </Switch>
            <div className="ml-3 self-end">{boolValue ? `${t('T_TRUE')}` : `${t('T_FALSE')}`}</div>
          </div>
        );

      case METADATA_OBJECT_DATA_TYPE_DATE:
      case METADATA_OBJECT_DATA_TYPE_DATE_ARRAY:
        return (
          <DatePicker
            onCalendarClose={() => {
              onDateChange(null, true);
            }}
            selected={tempDate ? new Date(tempDate) : new Date()}
            disabledKeyboardNavigation
            isClearable={
              type === METADATA_OBJECT_DATA_TYPE_DATE &&
              defaultItems !== '' &&
              !isUndefined(defaultItems)
            }
            dateFormat={`yyyy-MM-dd`}
            scrollableYearDropdown
            showMonthDropdown
            showYearDropdown
            yearDropdownItemNumber={100}
            onChange={(date: any) => {
              onDateChange(date, date ? false : true); //If Date does not exist it is "clear" flow.
            }}
            customInput={
              type === METADATA_OBJECT_DATA_TYPE_DATE ? (
                <DateInputField />
              ) : (
                <DateInputFieldArray values={defaultItems || []} setValues={setFormData} />
              )
            }
          />
        );

      case METADATA_OBJECT_DATA_TYPE_DATETIME:
      case METADATA_OBJECT_DATA_TYPE_DATETIME_ARRAY:
        return (
          <DatePicker
            onCalendarClose={() => {
              onDateChange(null, true);
            }}
            selected={tempDate ? new Date(tempDate) : new Date()}
            disabledKeyboardNavigation
            showTimeSelect
            showMonthDropdown
            scrollableYearDropdown={true}
            isClearable={
              type === METADATA_OBJECT_DATA_TYPE_DATETIME &&
              defaultItems !== '' &&
              !isUndefined(defaultItems)
            }
            showYearDropdown
            yearDropdownItemNumber={100}
            timeIntervals={30}
            dateFormat={'yyyy-MM-dd h:mm aa'}
            onChange={(date: any) => {
              onDateChange(date, date ? false : true); //If Date does not exist it is "clear" flow.
            }}
            customInput={
              type === METADATA_OBJECT_DATA_TYPE_DATETIME ? (
                <DateInputField />
              ) : (
                <DateInputFieldArray
                  values={
                    (defaultItems &&
                      defaultItems.map((date: any) =>
                        moment(date).format(AppConstants.DEFAULT_DATE_FORMAT)
                      )) ||
                    []
                  }
                  setValues={setFormData}
                />
              )
            }
          />
        );

      case METADATA_OBJECT_DATA_TYPE_TIME:
      case METADATA_OBJECT_DATA_TYPE_TIME_ARRAY:
        return (
          <DatePicker
            onCalendarClose={() => {
              onDateChange(null, true);
            }}
            selected={
              tempDate
                ? new Date(
                    `01/01/1970 ${moment(
                      tempDate,
                      AppConstants.DEFAULT_METADATA_INSTANCE_TIME_FORMAT
                    ).format('HH:mm:ss')}`
                  )
                : new Date()
            }
            disabledKeyboardNavigation
            showTimeSelect
            scrollableYearDropdown={true}
            showTimeSelectOnly
            isClearable={
              type === METADATA_OBJECT_DATA_TYPE_TIME &&
              defaultItems !== '' &&
              !isUndefined(defaultItems)
            }
            showMonthDropdown
            showYearDropdown
            yearDropdownItemNumber={100}
            timeIntervals={30}
            dateFormat={`h:mm aa`}
            onChange={(date: any) => {
              onDateChange(date, date ? false : true); //If Date does not exist it is "clear" flow.
            }}
            customInput={
              type === METADATA_OBJECT_DATA_TYPE_TIME ? (
                <DateInputField />
              ) : (
                <DateInputFieldArray
                  values={
                    (defaultItems &&
                      defaultItems.map((date: any) => moment(date, 'h:mm aa').format('hh:mm A'))) ||
                    []
                  }
                  setValues={setFormData}
                />
              )
            }
          />
        );

      case METADATA_OBJECT_DATA_TYPE_STRING:
        return (
          <TextArea
            id={name}
            data-testid={`textfield`}
            name={name}
            className={`!w-full rounded border px-3 py-2 ${
              defaultItems?.length > 255 && '!resize-y'
            }`}
            maxLength={maxLength}
            // required={isRequired}
            value={defaultItems}
            onChange={(e: any) => {
              setFormData({
                ...formData,
                default: e.target.value
              });
            }}
            disabled={disabled}
            rows={1}
            cols={20}
          />
        );
      case METADATA_OBJECT_DATA_TYPE_STRING_ARRAY:
      case METADATA_OBJECT_DATA_TYPE_NUMBER_ARRAY:
      case METADATA_OBJECT_DATA_TYPE_INTEGER_ARRAY:
        return isArray(defaultItems) && defaultItems.length > 0 ? (
          defaultItems.map((value, index) => (
            <div className="mb-3 flex flex-col">
              <div className="flex flex-row">
                <p className="font-bold">[{index}]</p>
                <button
                  className="ml-2  hover:text-gray-500 focus:outline-none"
                  data-testid="String[]-removeElement"
                  onClick={() => {
                    removeDefaultArrItem(index);
                  }}
                >
                  <XIcon className="h-6 w-6 text-black" />
                </button>
              </div>

              <InputField
                key={`primitive-array-${index}`}
                type={
                  [
                    METADATA_OBJECT_DATA_TYPE_NUMBER_ARRAY,
                    METADATA_OBJECT_DATA_TYPE_INTEGER_ARRAY
                  ].includes(type)
                    ? 'number'
                    : 'text'
                }
                id={`${name}-${index}`}
                data-testid={`textfield-${index}`}
                className={`!w-full rounded border px-3 py-2`}
                required={false}
                fieldKey={'default'}
                formData={{ default: value } as any}
                formError={formError}
                label=""
                placeholder={''}
                setFormData={(newValue: any) => {
                  let updatedFormData = { ...formData };
                  if (type === METADATA_OBJECT_DATA_TYPE_STRING_ARRAY) {
                    updatedFormData.default[index] = newValue.default;
                    setFormData(updatedFormData);
                  } else {
                    let parsedValue = newValue.default;

                    if (newValue.default !== '') {
                      if (type === METADATA_OBJECT_DATA_TYPE_INTEGER_ARRAY) {
                        if (Number.isInteger(parseFloat(newValue.default))) {
                          parsedValue = parseFloat(newValue.default);
                        } else {
                          parsedValue = '';
                        }
                      } else {
                        parsedValue = parseFloat(newValue.default);
                        if (isNaN(parsedValue)) {
                          parsedValue = '';
                        }
                      }
                    }

                    updatedFormData.default[index] = parsedValue;
                    setFormData(updatedFormData);
                  }
                }}
                disabled={disabled}
              />
            </div>
          ))
        ) : (
          <ErrorLabel>{t('T_ADD_DEFAULT_VALUE_EMPTY')}</ErrorLabel>
        );

      default:
        return (
          <InputField
            type={
              [METADATA_OBJECT_DATA_TYPE_NUMBER, METADATA_OBJECT_DATA_TYPE_INTEGER].includes(type)
                ? 'number'
                : 'text'
            }
            id={name}
            data-testid={`textfield`}
            className={`!w-full rounded border px-3 py-2`}
            required={false}
            fieldKey={'default'}
            formData={formData}
            formError={formError}
            label=""
            placeholder={''}
            setFormData={(value: any) => {
              let parsedValue = value.default;

              if (value.default !== '') {
                if (type === METADATA_OBJECT_DATA_TYPE_INTEGER) {
                  if (Number.isInteger(parseFloat(value.default))) {
                    parsedValue = parseFloat(value.default);
                  } else {
                    parsedValue = '';
                  }
                } else {
                  parsedValue = parseFloat(value.default);
                  if (isNaN(parsedValue)) {
                    parsedValue = '';
                  }
                }
              }

              setFormData({
                ...formData,
                default: parsedValue
              });
            }}
            disabled={disabled}
          />
        );
    }
  };

  const addToDefaultArray = () => {
    const current = cloneDeep(defaultItems);

    if (!isArray(current)) {
      setFormData({ ...formData, default: [''] });
    } else {
      current.push('');
      setFormData({
        ...formData,
        default: current
      });
    }
  };

  return (
    <div className="mb-5 flex w-full flex-1 flex-col">
      <div className="mb-3 flex flex-1 flex-row">
        <Label>{`${t('T_DEFAULT_VALUE')}`}</Label>
        {[
          METADATA_OBJECT_DATA_TYPE_STRING_ARRAY,
          METADATA_OBJECT_DATA_TYPE_NUMBER_ARRAY,
          METADATA_OBJECT_DATA_TYPE_INTEGER_ARRAY
        ].includes(type) && (
          <button
            className="ml-2  hover:text-gray-500 focus:outline-none"
            onClick={addToDefaultArray}
            data-testid="String[]-addElement"
          >
            <PlusIcon className="mb-1 h-5 w-5 text-black" />
          </button>
        )}
      </div>
      {renderPrimitiveField()}
    </div>
  );
};

export default DefaultValueInput;
